import { getState, modifyState } from '~src/state';
import { getCanvasBaseZoom, resizeDimensions } from '~loader/loader-utils';
import {
  calculateFormatDpi,
  formatHasOwnDimensions,
  isPositiveFiniteNumber,
} from '~loader/loader-utils/calculateFormat';
import { resizeEditorCanvas } from '~core/resizeEditorCanvas';

const DEFAULT_DIMENSION_DPI = 300;

export const resolveTemplateCanvasFormat = (
  template,
  {
    defaultDpi = window.editorLoader?.defaultDPI || DEFAULT_DIMENSION_DPI,
    fallbackDpi = window.editorLoader?.sourceDPI || defaultDpi,
  } = {}
) => {
  const format = template?.editor_project_formats_pointer;
  const linkedDefaultFormat = format?.editor_project_formats_default_pointer;
  const topLevelDefaultFormat =
    template?.editor_project_formats_default_pointer;
  const formatDefault =
    linkedDefaultFormat?.objectId &&
    linkedDefaultFormat.objectId === topLevelDefaultFormat?.objectId
      ? {
          ...topLevelDefaultFormat,
          ...linkedDefaultFormat,
        }
      : linkedDefaultFormat || topLevelDefaultFormat;
  const dimensionsFormat = formatHasOwnDimensions(format)
    ? format
    : [linkedDefaultFormat, topLevelDefaultFormat].find(formatHasOwnDimensions);

  if (!dimensionsFormat) return null;

  const dpi = calculateFormatDpi(
    {
      editor_project_formats_pointer: format,
      editor_project_formats_default_pointer:
        topLevelDefaultFormat || linkedDefaultFormat,
    },
    fallbackDpi
  );

  if (!isPositiveFiniteNumber(defaultDpi) || !isPositiveFiniteNumber(dpi)) {
    return null;
  }

  const { width, height } = resizeDimensions(
    Number(dimensionsFormat.width),
    Number(dimensionsFormat.height),
    Number(defaultDpi),
    Number(dpi)
  );

  if (!isPositiveFiniteNumber(width) || !isPositiveFiniteNumber(height)) {
    return null;
  }

  return {
    format: format?.objectId
      ? formatDefault
        ? {
            ...format,
            editor_project_formats_default_pointer: formatDefault,
          }
        : format
      : undefined,
    formatDefault,
    dpi,
    width,
    height,
  };
};

export const applyTemplateFormatToCanvas = async (template) => {
  await modifyState('selected.template', template);

  const resolvedFormat = resolveTemplateCanvasFormat(template);
  if (!resolvedFormat) return null;

  const currentFormat = getState('selected.formato');
  const templateFormat = template?.editor_project_formats_pointer;
  const shouldAdoptTemplateFormat =
    formatHasOwnDimensions(templateFormat) ||
    formatHasOwnDimensions(currentFormat);

  // Ordinary templates in the same format lineage keep the project's existing
  // org wrapper. A project that currently uses real custom dimensions must
  // instead adopt the target template geometry, including when returning to a
  // standard/default template.
  if (!shouldAdoptTemplateFormat) return null;

  const { format, formatDefault, width, height } = resolvedFormat;
  const zoom = getCanvasBaseZoom(width, height);

  await modifyState('selected.formato', format || {});
  await modifyState('selected.formatoDefault', formatDefault);
  await resizeEditorCanvas({ width, height, zoom });

  return {
    ...resolvedFormat,
    zoom,
  };
};
